import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import SectionTitle from "@/components/base/SectionTitle";
import { gasCorrosionPriceList } from "@/mocks/pricelist";
import { supabase } from "@/lib/supabase";

export default function FreeTrial() {
  const [formStatus, setFormStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("loading");
    setFormError("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("company_alt") as string) || "";
    if (honeypot.trim() !== "") {
      setFormStatus("success");
      form.reset();
      return;
    }

    const payload = {
      name: (formData.get("name") as string) || "",
      company: (formData.get("company") as string) || "",
      email: (formData.get("email") as string) || "",
      interest: (formData.get("interest") as string) || "",
    };

    try {
      const { data, error } = await supabase.functions.invoke("send-trial-email", {
        body: payload,
      });

      if (error) {
        setFormStatus("error");
        setFormError("送信に失敗しました。再度お試しください。");
        return;
      }

      if (data && data.success) {
        setFormStatus("success");
        form.reset();
      } else {
        setFormStatus("error");
        setFormError((data?.error as string) || "送信に失敗しました。再度お試しください。");
      }
    } catch {
      setFormError("ネットワークエラーが発生しました。時間をおいて再度お試しください。");
      setFormStatus("error");
    }
  };

  const benefits = [
    {
      icon: "ri-flask-line",
      title: "実際の腐食ガス環境を体験",
      desc: "H₂S、SO₂など実際の腐食性ガスを使用した試験を体験いただけます。製品がどのような環境ストレスを受けるのか、目で見て確かめられます。",
    },
    {
      icon: "ri-microscope-line",
      title: "試験結果レポートを無料提供",
      desc: "体験試験の結果は、専門スタッフがわかりやすくレポートにまとめてお渡しします。表面状態の観察結果など、具体的なデータをご確認いただけます。",
    },
    {
      icon: "ri-user-voice-line",
      title: "技術コンサルティング付き",
      desc: "試験結果をもとに、当社の技術スタッフが改善の方向性や対策案についてアドバイスいたします。お客様の製品課題に合わせた具体的なご提案が可能です。",
    },
    {
      icon: "ri-price-tag-3-line",
      title: "完全無料・お気軽に",
      desc: "試験費用・レポート作成・コンサルティングまで、すべて無料でご提供します。まずはお試しいただき、当社の試験品質とサービスの価値を実感してください。",
    },
  ];

  const singleGas = gasCorrosionPriceList.groups[0].items[0];
  const normalPrice = parseInt(singleGas.baseFee.replace(/,/g, ''), 10) + 24 * parseInt(singleGas.fee.replace(/,/g, ''), 10);

  const flowSteps = [
    {
      step: "01",
      title: "事前登録",
      desc: "下記フォームよりメールアドレスをご登録ください。サービス開始時に優先してご案内いたします。",
      icon: "ri-mail-send-line",
    },
    {
      step: "02",
      title: "ヒアリング",
      desc: "担当スタッフよりご連絡し、試験内容やご希望の条件、サンプルについて詳しくお伺いします。",
      icon: "ri-phone-line",
    },
    {
      step: "03",
      title: "サンプル送付",
      desc: "試験したい部品や材料をお送りください。少量のサンプルで結構です。",
      icon: "ri-truck-line",
    },
    {
      step: "04",
      title: "試験実施・結果報告",
      desc: "約1〜2週間で試験を実施し、結果レポートと改善アドバイスをお届けします。",
      icon: "ri-file-chart-line",
    },
  ];

  return (
    <PageLayout>
      {/* Hero */}
      <div className="relative py-14 md:py-24 bg-primary-700 overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20industrial%20laboratory%20interior%20with%20scientific%20testing%20equipment%2C%20clean%20white%20and%20bright%20atmosphere%2C%20professional%20yet%20welcoming%20environment%2C%20soft%20natural%20lighting%2C%20minimal%20composition%2C%20warm%20academic%20aesthetic&width=1600&height=700&seq=freetrial-hero&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/25" />
        <div className="relative z-10 w-full px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <nav className="mb-6">
              <ol className="flex flex-wrap items-center gap-2 text-sm text-background-300">
                <li>
                  <Link to="/" className="hover:text-background-100 transition-colors">ホーム</Link>
                </li>
                <li><i className="ri-arrow-right-s-line text-xs" /></li>
                <li>
                  <Link to="/service" className="hover:text-background-100 transition-colors">サービス</Link>
                </li>
                <li><i className="ri-arrow-right-s-line text-xs" /></li>
                <li className="text-background-100">ガス腐食試験 無料体験</li>
              </ol>
            </nav>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100/80 border border-amber-300 text-amber-700 text-xs font-bold">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                近日開始予定
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/95 border border-background-200 shadow-lg">
                <span className="text-xs text-foreground-400 line-through font-medium">通常 {normalPrice.toLocaleString()}円</span>
                <span className="w-0.5 h-4 bg-background-300" />
                <span className="text-sm font-black text-primary-600">無料</span>
              </div>
            </div>
            <h1 className="text-3xl md:text-5xl font-black text-background-50 leading-tight mb-4">
              ガス腐食試験
              <br />
              無料体験サービス
            </h1>
            <p className="text-background-200/80 text-sm md:text-base max-w-lg leading-relaxed mb-8">
              あなたの製品が腐食性ガスにどれだけ耐えられるか、実際に試験して確かめてみませんか？
              試験実施からレポート作成まで、すべて無料でご提供します。
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <a
                href="#notify-form"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-background-50 text-foreground-950 font-bold text-sm hover:bg-background-100 transition-all duration-300 whitespace-nowrap"
              >
                <span>開始通知を受け取る</span>
                <i className="ri-arrow-down-line" />
              </a>
              <span className="text-background-300/70 text-xs leading-relaxed">
                登録は30秒 ・ 完全無料 ・ 営業電話なし<br />
                ※ サービス開始は準備が整い次第、登録者様へ優先的にご案内いたします。
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-5xl mx-auto">
          {/* Intro */}
          <SectionTitle
            title="なぜ無料体験なのか？"
            subtitle="まずは実際の試験品質を体感していただきたいから"
            centered
          />
          <div className="max-w-3xl mx-auto mt-5 text-center">
            <p className="text-sm text-foreground-600 leading-relaxed">
              ガス腐食試験は、電子部品やコネクタの信頼性評価に欠かせない重要な試験です。
              しかし「どんな試験なのかイメージがわかない」「費用対効果が読めない」という声を多くいただいてきました。
            </p>
            <p className="text-sm text-foreground-600 leading-relaxed mt-3">
              そこでファクトケイでは、実際の試験を無料で体験いただけるサービスをご用意いたしました。
              試験のプロセスやレポートのクオリティ、技術コンサルティングの価値を、
              リスクゼロでお試しいただけます。
            </p>
          </div>

          {/* Trial Conditions */}
          <div className="mt-14">
            <SectionTitle
              title="無料体験の試験条件"
              subtitle="お選びいただける2つのコース、どちらも24時間の短期集中試験"
              centered
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
              <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70 hover:border-primary-200 transition-all duration-300">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-test-tube-line text-lg" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground-900">H₂S コース</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2.5 border-b border-background-200/50">
                    <span className="text-sm text-foreground-600">試験ガス</span>
                    <span className="text-sm font-bold text-foreground-900">H₂S（硫化水素）</span>
                  </div>
                  <div className="flex items-center justify-between py-2.5 border-b border-background-200/50">
                    <span className="text-sm text-foreground-600">ガス濃度</span>
                    <span className="text-sm font-bold text-foreground-900">3 ppm</span>
                  </div>
                  <div className="flex items-center justify-between py-2.5 border-b border-background-200/50">
                    <span className="text-sm text-foreground-600">試験時間</span>
                    <span className="text-sm font-bold text-foreground-900">24 時間</span>
                  </div>
                  <div className="flex items-center justify-between py-2.5 border-b border-background-200/50">
                    <span className="text-sm text-foreground-600">温度</span>
                    <span className="text-sm font-bold text-foreground-900">25 ℃ 〜 65 ℃</span>
                  </div>
                  <div className="flex items-center justify-between py-2.5">
                    <span className="text-sm text-foreground-600">湿度</span>
                    <span className="text-sm font-bold text-foreground-900">75 ％ 〜 95 ％</span>
                  </div>
                </div>
                <div className="mt-5 pt-4 border-t border-background-200/60">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-foreground-500">通常料金（受託試験）</span>
                    <span className="text-sm text-foreground-400 line-through">{normalPrice.toLocaleString()} 円</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-500">無料体験</span>
                    <span className="text-base font-black text-primary-600">0 円</span>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70 hover:border-accent-200 transition-all duration-300">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className="ri-test-tube-line text-lg" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground-900">SO₂ コース</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2.5 border-b border-background-200/50">
                    <span className="text-sm text-foreground-600">試験ガス</span>
                    <span className="text-sm font-bold text-foreground-900">SO₂（二酸化硫黄）</span>
                  </div>
                  <div className="flex items-center justify-between py-2.5 border-b border-background-200/50">
                    <span className="text-sm text-foreground-600">ガス濃度</span>
                    <span className="text-sm font-bold text-foreground-900">5 ppm</span>
                  </div>
                  <div className="flex items-center justify-between py-2.5 border-b border-background-200/50">
                    <span className="text-sm text-foreground-600">試験時間</span>
                    <span className="text-sm font-bold text-foreground-900">24 時間</span>
                  </div>
                  <div className="flex items-center justify-between py-2.5 border-b border-background-200/50">
                    <span className="text-sm text-foreground-600">温度</span>
                    <span className="text-sm font-bold text-foreground-900">25 ℃ 〜 65 ℃</span>
                  </div>
                  <div className="flex items-center justify-between py-2.5">
                    <span className="text-sm text-foreground-600">湿度</span>
                    <span className="text-sm font-bold text-foreground-900">75 ％ 〜 95 ％</span>
                  </div>
                </div>
                <div className="mt-5 pt-4 border-t border-background-200/60">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-foreground-500">通常料金（受託試験）</span>
                    <span className="text-sm text-foreground-400 line-through">{normalPrice.toLocaleString()} 円</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-500">無料体験</span>
                    <span className="text-base font-black text-accent-600">0 円</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="mt-16">
            <SectionTitle
              title="無料体験で得られる4つの価値"
              subtitle="試験実施から改善提案まで、まるごと無料"
              centered
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6 mt-8">
              {benefits.map((b, i) => (
                <div
                  key={i}
                  className="bg-background-50 rounded-xl p-6 md:p-7 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 mb-4">
                    <i className={`${b.icon} text-xl`} />
                  </div>
                  <h3 className="text-base font-bold text-foreground-900 mb-2">{b.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Target */}
          <div className="mt-16 bg-accent-50 rounded-xl p-6 md:p-8 border border-accent-100">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                <i className="ri-user-star-line text-lg" />
              </div>
              <h3 className="text-base font-bold text-foreground-900">こんな方におすすめです</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                "新規部品の信頼性を事前に評価したい設計者の方",
                "部品メーカーの切替えを検討されている購買・品質管理の方",
                "海外向け製品の耐環境性能を確認したい方",
                "接触不良の原因を特定したい技術者の方",
                "腐食試験の導入を検討しているが、まずは試してみたい方",
                "ガス腐食試験というものを初めて知った方",
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <i className="ri-check-line text-accent-600 mt-0.5 flex-shrink-0 text-sm" />
                  <span className="text-sm text-foreground-700">{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Flow */}
          <div className="mt-16">
            <SectionTitle
              title="ご利用の流れ"
              subtitle="お申し込みから結果報告まで、わずか4ステップ"
              centered
            />
            <div className="mt-8 flex flex-col md:flex-row items-stretch gap-0">
              {flowSteps.map((f, i) => (
                <div key={i} className="flex flex-col md:flex-row flex-1">
                  <div className="flex-1 bg-background-50 rounded-xl p-5 border border-background-200/70 hover:border-primary-300 transition-all duration-300">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-bold text-base">
                          {f.step}
                        </div>
                      </div>
                      <div>
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500 mb-2">
                          <i className={`${f.icon} text-lg`} />
                        </div>
                        <h4 className="text-base font-bold text-foreground-900 mb-1">{f.title}</h4>
                        <p className="text-xs text-foreground-600 leading-relaxed">{f.desc}</p>
                      </div>
                    </div>
                  </div>
                  {i < 3 && (
                    <div className="flex items-center justify-center py-3 md:py-0 md:px-3">
                      <i className="ri-arrow-down-line md:hidden text-primary-400 text-2xl" />
                      <i className="ri-arrow-right-line hidden md:block text-primary-400 text-2xl" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Notice: Machine Availability */}
          <div className="mt-14">
            <div className="max-w-2xl mx-auto bg-amber-50 rounded-xl p-5 md:p-6 border border-amber-200 flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-amber-100 text-amber-600">
                <i className="ri-information-line text-xl" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-foreground-900 mb-2">
                  試験機の混み具合について
                </h4>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  試験機の稼働状況によってはお待たせしてしまう可能性がございます。
                  お急ぎのご依頼やご希望の試験時期がお決まりの場合は、まずはお問い合わせフォームまたはお電話にてご相談ください。
                  現在の空き状況や最短での試験実施可能時期を、担当者よりご案内いたします。
                </p>
              </div>
            </div>
          </div>

          {/* Notification Form */}
          <div className="mt-16" id="notify-form">
            <SectionTitle
              title="開始通知のご登録"
              subtitle="サービス開始時にメールにてご案内いたします。お気軽にご登録ください。"
              centered
            />
            <div className="max-w-2xl mx-auto mt-8 bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70">
              <form onSubmit={handleSubmit}>
                {/* お名前 */}
                <div className="mb-5">
                  <label htmlFor="name" className="block text-sm font-bold text-foreground-900 mb-1.5">
                    お名前 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="山田 太郎"
                  />
                </div>

                {/* メールアドレス */}
                <div className="mb-5">
                  <label htmlFor="email" className="block text-sm font-bold text-foreground-900 mb-1.5">
                    メールアドレス <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="example@factk.co.jp"
                  />
                </div>

                {/* 会社名 */}
                <div className="mb-5">
                  <label htmlFor="company" className="block text-sm font-bold text-foreground-900 mb-1.5">
                    会社名
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="例：ファクトケイ株式会社"
                  />
                </div>

                {/* 興味のあるコース */}
                <div className="mb-5">
                  <label htmlFor="interest" className="block text-sm font-bold text-foreground-900 mb-1.5">
                    興味のある試験コース
                  </label>
                  <select
                    id="interest"
                    name="interest"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors appearance-none"
                    style={{ backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")", backgroundPosition: "right 0.75rem center", backgroundRepeat: "no-repeat", backgroundSize: "1.25rem" }}
                    defaultValue=""
                  >
                    <option value="" disabled>選択してください</option>
                    <option value="H2S 3ppm コース">H₂S 3ppm コース</option>
                    <option value="SO2 5ppm コース">SO₂ 5ppm コース</option>
                    <option value="両方">両方に興味がある</option>
                    <option value="未定">まだ決まっていない</option>
                  </select>
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="absolute opacity-0 pointer-events-none"
                  style={{ position: "absolute", left: "-9999px", top: "-9999px" }}
                />

                {/* Privacy note */}
                <div className="mb-6 p-4 rounded-lg bg-background-100 border border-background-200/60">
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    ご入力いただいた個人情報は、本無料体験サービスの開始案内にのみ使用し、
                    第三者への提供は一切行いません。また、事前の承諾なく営業活動を行うことは一切ございませんので、ご安心ください。
                  </p>
                </div>

                {/* Status */}
                {formStatus === "success" && (
                  <div className="mb-6 p-4 rounded-lg bg-primary-50 border border-primary-200 text-center">
                    <i className="ri-check-double-line text-primary-600 text-2xl block mb-2" />
                    <p className="text-sm font-bold text-primary-800 mb-1">ご登録を受け付けました</p>
                    <p className="text-xs text-primary-600">サービス開始時に、ご登録のメールアドレスへご案内をお送りいたします。</p>
                  </div>
                )}
                {formStatus === "error" && (
                  <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 text-center">
                    <i className="ri-error-warning-line text-red-500 text-2xl block mb-2" />
                    <p className="text-sm font-bold text-red-700 mb-1">送信に失敗しました</p>
                    <p className="text-xs text-red-600">{formError}</p>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={formStatus === "loading"}
                  className="w-full py-3.5 rounded-lg bg-primary-500 text-background-50 font-bold text-sm hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  {formStatus === "loading" ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin" />
                      送信中...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-send-plane-line" />
                      開始通知を登録する
                    </span>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-16">
            <SectionTitle
              title="よくあるご質問"
              subtitle="無料体験サービスに関するQ&A"
              centered
            />
            <div className="max-w-2xl mx-auto mt-8 space-y-3">
              {[
                {
                  q: "本当に完全無料ですか？",
                  a: "はい。試験実施・試験報告書（簡易版）作成の費用は一切いただきません。なお、試験サンプルをお送りいただく際の送料のみご負担をお願いしております。また、後日有料サービスへの勧誘を行うこともございませんのでご安心ください。",
                },
                {
                  q: "どのようなサンプルが必要ですか？",
                  a: "コネクタ、スイッチ、リレー、端子などの接点部品を少量ご用意ください。具体的な点数や形状については、ヒアリング時にお伺いします。",
                },
                {
                  q: "結果が出るまでどのくらいかかりますか？",
                  a: "サンプル到着後、通常1〜2週間程度で結果レポートをお届けします。お急ぎの場合はご相談ください。",
                },
                {
                  q: "試験条件は選べますか？",
                  a: "無料体験ではH₂S 3ppmコースとSO₂ 5ppmコースの2種類をご用意しています。試験時間はいずれも24時間、温度25℃〜65℃、湿度75%〜95%の範囲で設定可能です。まずはこの標準コースをお試しいただき、ご満足いただけましたら有料の受託試験にてご希望の条件で試験を承ります。",
                },
                {
                  q: "サービス開始はいつですか？",
                  a: "現在、万全の体制でご提供できるよう準備を進めております。サービス開始日が決まり次第、ご登録いただいた方へ優先的にメールにてご案内いたします。まずは通知登録フォームよりご登録いただけますと幸いです。",
                },
                {
                  q: "試験結果を二次利用（社外提出など）できますか？",
                  a: "無料体験で得られた試験結果は、お客様の社内検討用としてご提供するものです。社外への提出・公開や、取引先へのエビデンスとしてのご利用（二次利用）は、基本的にお受けしておりません。二次利用をご希望の場合は、正式な試験報告書を作成する有料の受託試験をご依頼ください。無料体験の結果をもとに、改めて正式試験をお申し込みいただくことも可能です。報告書作成費用については別途お見積りいたします。",
                },
              ].map((faq, i) => (
                <details key={i} className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between gap-3 px-5 py-4 cursor-pointer list-none">
                    <span className="text-sm font-bold text-foreground-900">{faq.q}</span>
                    <i className="ri-add-line text-foreground-400 group-open:hidden text-lg flex-shrink-0" />
                    <i className="ri-subtract-line text-foreground-400 hidden group-open:block text-lg flex-shrink-0" />
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>

          {/* Contact CTA */}
          <div className="mt-16 bg-primary-700 rounded-xl p-8 md:p-10 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20technical%20background%20with%20soft%20geometric%20shapes%2C%20clean%20minimal%20design%2C%20subtle%20gradient%20warm%20light%20tones%2C%20modern%20professional%20atmosphere&width=1200&height=400&seq=freetrial-cta&orientation=landscape"
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative z-10">
              <h3 className="text-xl md:text-2xl font-bold text-background-50 mb-3">
                まずはお気軽にお問い合わせください
              </h3>
              <p className="text-sm text-background-200 max-w-xl mx-auto mb-6">
                サービス開始時にいち早くご案内を受け取りたい方は、
                上記フォームよりご登録ください。試験内容についてのご質問も受け付けております。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="#notify-form"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-background-50 text-primary-700 font-semibold text-sm hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  <i className="ri-edit-line" />
                  登録フォームへ
                </a>
                <Link
                  to="/service"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-background-300 text-background-100 font-semibold text-sm hover:bg-background-50/10 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line" />
                  サービスページへ戻る
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}